import {
	type AgContextAction,
	AgContextMenu,
} from "@/components/accounting/ag-context-menu";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { ChevronDown, Plus, Search } from "lucide-react";
import type { JournalEntry } from "@/gen/omnios/v1/accounting_pb";
import {
	useBatchPostEntries,
	useBatchReverseEntries,
} from "@/hooks/use-accounting";
import { useAccountingShortcuts } from "@/hooks/use-accounting-shortcuts";
import { formatDate } from "@/lib/format";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useGridLayout } from "@/lib/use-grid-layout";
import { useServerGrid } from "@/lib/use-server-grid";
import { accountingClient } from "@/lib/accounting-client";

import type {
	ColDef,
	ValueFormatterParams,
} from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useGridSelection } from "@/hooks/use-grid-selection";

interface JournalSearch {
	status?: string;
	period?: string;
	account?: string;
}

export const Route = createFileRoute("/accounting/journal/")({
	component: JournalPage,
	validateSearch: (search: Record<string, unknown>): JournalSearch => ({
		status: typeof search.status === "string" ? search.status : undefined,
		period: typeof search.period === "string" ? search.period : undefined,
		account: typeof search.account === "string" ? search.account : undefined,
	}),
});

function JournalPage() {
	const { t } = useTranslation("accounting");
	const navigate = useNavigate();
	const search = Route.useSearch();
	const batchPost = useBatchPostEntries();
	const batchReverse = useBatchReverseEntries();
	const { onGridReady, onStateChanged } = useGridLayout("journal");
	const { selectedRows, hasSelection, onSelectionChanged, ActionBar } =
		useGridSelection<JournalEntry>();
	const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
	const statusFilter = search.status ?? "";
	const periodFilter = search.period ?? "";
	const extraFilter = useMemo(() => {
		const f: Record<string, unknown> = {};
		if (statusFilter) f.status = Number(statusFilter);
		if (periodFilter) f.periodId = periodFilter;
		return f;
	}, [statusFilter, periodFilter]);

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<JournalEntry>({
		colToField: {
			validDate: "valid_date",
		},
		fetchRows: async ({ pageSize, pageToken, sort, filter, search: searchStr }) => {
			const resp = await accountingClient.listEntries({
				page: { pageSize, pageToken },
				sort: sort ? { field: sort.field, descending: sort.descending } : undefined,
				filter: filter as never,
				search: searchStr ?? "",
				status: statusFilter ? Number(statusFilter) : undefined,
				periodId: periodFilter || undefined,
			});
			return {
				rows: resp.entries,
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		pageSize: 100,
		extraFilter,
	});

	// ── Column definitions ──
	const columnDefs = useMemo<ColDef<JournalEntry>[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "entryNumber",
				headerName: "№",
				width: 90,
				pinned: "left",
				cellClass: "font-mono",
			},
			{
				field: "validDate",
				headerName: t("entry_date"),
				width: 90,
				filter: "agDateColumnFilter",
				valueFormatter: (p: ValueFormatterParams<JournalEntry>) => {
					const d = p.data?.validDate;
					return d ? formatDate(d.toDate(), "dd.MM.yyyy") : "";
				},
			},
			{
				field: "description",
				headerName: t("entry_description"),
				flex: 2,
				minWidth: 150,
			},
			{
				field: "status",
				headerName: t("entry_status"),
				width: 90,
				filter: false,
				cellRenderer: (p: { value: number | undefined }) => {
					if (p.value == null) return "";
					const labels: Record<number, string> = {
						1: t("status_draft"),
						2: t("status_pending"),
						3: t("status_approved"),
						4: t("status_posted"),
						5: t("status_reversed"),
					}
					const colors: Record<number, string> = {
						1: "hsl(var(--muted-foreground))",
						4: "hsl(var(--chart-2))",
						5: "hsl(var(--destructive))",
					}
					return <span style={{ color: colors[p.value] || "hsl(var(--muted-foreground))" }}>{labels[p.value] || "—"}</span>;
				},
			},
			{
				field: "category",
				headerName: t("entry_category"),
				width: 100,
				filter: false,
				hide: true,
			},
			{
				headerName: t("total_debit"),
				flex: 1,
				minWidth: 100,
				type: "rightAligned",
				filter: false,
				cellClass: "font-tabular",
				valueGetter: (p) => {
					const lines = p.data?.lines ?? [];
					return lines
						.filter((l) => l.side === 1)
						.reduce((s, l) => s + Number.parseFloat(l.amount || "0"), 0);
				},
				valueFormatter: (p: ValueFormatterParams) =>
					p.value ? formatAmount(p.value) : "—",
			},
			{
				headerName: t("total_credit"),
				flex: 1,
				minWidth: 100,
				type: "rightAligned",
				filter: false,
				cellClass: "font-tabular",
				valueGetter: (p) => {
					const lines = p.data?.lines ?? [];
					return lines
						.filter((l) => l.side === 2)
						.reduce((s, l) => s + Number.parseFloat(l.amount || "0"), 0);
				},
				valueFormatter: (p: ValueFormatterParams) =>
					p.value ? formatAmount(p.value) : "—",
			},
			{
				field: "currency",
				headerName: t("entry_currency"),
				width: 55,
				hide: true,
			},
			{
				field: "basis",
				headerName: t("basis"),
				width: 70,
				hide: true,
			},
			{
				colId: "dim_cost_center",
				headerName: t("dim_cost_center", { defaultValue: "Центр затрат" }),
				width: 120,
				hide: true,
				valueGetter: (p) => {
					const dims = p.data?.lines?.[0]?.dimensions;
					return dims?.fields?.cost_center?.kind?.value ?? "";
				},
			},
			{
				colId: "dim_department",
				headerName: t("dim_department", { defaultValue: "Подразделение" }),
				width: 120,
				hide: true,
				valueGetter: (p) => {
					const dims = p.data?.lines?.[0]?.dimensions;
					return dims?.fields?.department?.kind?.value ?? "";
				},
			},
			{
				colId: "dim_project",
				headerName: t("dim_project", { defaultValue: "Проект" }),
				width: 120,
				hide: true,
				valueGetter: (p) => {
					const dims = p.data?.lines?.[0]?.dimensions;
					return dims?.fields?.project?.kind?.value ?? "";
				},
			},
			{
				colId: "dim_counterparty",
				headerName: t("dim_counterparty", { defaultValue: "Контрагент" }),
				width: 140,
				hide: true,
				valueGetter: (p) => {
					const dims = p.data?.lines?.[0]?.dimensions;
					return dims?.fields?.counterparty?.kind?.value ?? "";
				},
			},
		],
		[t],
	)

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	)

	const onRowClicked = useCallback(
		(e: { data: JournalEntry | undefined }) => {
			if (e.data) {
				navigate({
					to: "/accounting/journal/$id",
					params: { id: e.data.id },
				})
			}
		},
		[navigate],
	)

	// ── Status bar items ──
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "selection",
				content: hasSelection
					? t("rows_selected", { count: selectedRows.length })
					: t("row_count", { count: totalRows }),
				align: "left" as const,
			},
		],
		[hasSelection, selectedRows.length, totalRows, t],
	)

	useWorkspacePage({ statusBar: statusBarItems });

	// ── Keyboard shortcuts ──
	useAccountingShortcuts(
		useMemo(
			() => ({
				"Ctrl+N": () => navigate({ to: "/accounting/journal/new" }),
				Enter: () => {
					if (selectedRows.length === 1) {
						navigate({
							to: "/accounting/journal/$id",
							params: { id: selectedRows[0].id },
						})
					}
				},
				Delete: () => {
					if (hasSelection) setReverseDialogOpen(true);
				},
				"Ctrl+D": () => {
					if (selectedRows.length === 1) {
						navigate({ to: "/accounting/journal/new" });
					}
				},
				F9: () => {
					if (hasSelection) {
						batchPost.mutate({ entryIds: selectedRows.map((r) => r.id) });
					}
				},
			}),
			[navigate, selectedRows, hasSelection, batchPost],
		),
	)

	const contextActions = useMemo<AgContextAction[]>(
		() => [
			{
				id: "open",
				label: t("context_open", { defaultValue: "Открыть" }),
				shortcut: "Enter",
				onClick: (row) =>
					navigate({
						to: "/accounting/journal/$id",
						params: { id: (row as JournalEntry).id },
					}),
			},
			{
				id: "duplicate",
				label: t("context_duplicate", { defaultValue: "Дублировать" }),
				shortcut: "Ctrl+D",
				onClick: () => navigate({ to: "/accounting/journal/new" }),
			},
			{
				id: "post",
				label: t("toolbar_post"),
				shortcut: "F9",
				onClick: (row) => batchPost.mutate({ entryIds: [(row as JournalEntry).id] }),
			},
			{
				id: "reverse",
				label: t("toolbar_reverse"),
				shortcut: "Del",
				onClick: () => setReverseDialogOpen(true),
				destructive: true,
			},
		],
		[t, navigate, batchPost],
	)

	return (
		<>
			<div className="flex flex-col h-full overflow-hidden">
				<div className="flex items-center gap-2 px-4 py-2 shrink-0">
					<div className="relative flex-1">
						<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
						<Input
							placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
							onChange={(e) =>
								gridRef.current?.api?.setGridOption(
									"quickFilterText",
									e.target.value,
								)
							}
							className="pl-9 h-9"
						/>
					</div>
					<Button
						variant="outline"
						size="sm"
						className="h-9"
						onClick={() => {
							batchPost.mutate(
								{ entryIds: selectedRows.map((r) => r.id) },
								{
									onSuccess: (res) => {
										toast.success(
											`${res.successCount} ${t("batch_posted", { defaultValue: "из" })} ${selectedRows.length} ${t("batch_posted_suffix", { defaultValue: "проведено" })}`,
										);
										refreshGrid();
									},
								},
							);
						}}
						disabled={!hasSelection || batchPost.isPending}
					>
						{t("toolbar_post")}
					</Button>
					<Button
						variant="outline"
						size="sm"
						className="h-9"
						onClick={() => setReverseDialogOpen(true)}
						disabled={!hasSelection}
					>
						{t("toolbar_reverse")}
					</Button>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" size="sm" className="h-9">
								{t("toolbar_print")}
								<ChevronDown className="h-3.5 w-3.5 ml-1 opacity-50" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem onClick={() => window.print()}>
								{t("print_list")}
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => window.print()}>
								{t("print_current")}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" size="sm" className="h-9">
								{t("toolbar_export")}
								<ChevronDown className="h-3.5 w-3.5 ml-1 opacity-50" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem
								onClick={() =>
									gridRef.current?.api?.exportDataAsCsv({
										fileName: "journal.csv",
									})
								}
							>
								Excel
							</DropdownMenuItem>
							<DropdownMenuItem
								onClick={() => gridRef.current?.api?.exportDataAsCsv()}
							>
								CSV
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
					<Button
						size="sm"
						className="h-9"
						onClick={() => navigate({ to: "/accounting/journal/new" })}
					>
						<Plus className="h-4 w-4 mr-1" />
						{t("toolbar_new")}
					</Button>
				</div>
				<AgContextMenu actions={contextActions}>
				<div
					className="flex-1 ag-theme-quartz"
					style={{ width: "100%" }}
				>
					<AgGridReact<JournalEntry>
						ref={gridRef}
						{...gridProps}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						localeText={AG_GRID_LOCALE_RU}
						rowSelection="multiple"
						onSelectionChanged={onSelectionChanged}
						onRowClicked={onRowClicked}
						onGridReady={onGridReady}
						onSortChanged={() => {
							onStateChanged();
							refreshGrid();
						}}
						onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
						onColumnMoved={onStateChanged}
						onColumnResized={onStateChanged}
						onColumnVisible={onStateChanged}
						suppressRowClickSelection
						rowClass="cursor-pointer"
						animateRows={false}
						rowHeight={28}
						headerHeight={32}
						getRowId={(p) => p.data?.id ?? ""}
						overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("empty_journal_title", { defaultValue: "Журнал пуст" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("empty_journal_desc", { defaultValue: "Нажмите Ctrl+N, чтобы создать проводку" })}</p></div>`}
						overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("loading")}...</span>`}
					/>
				</div>
				</AgContextMenu>
			</div>

			<ActionBar />
			{/* Reverse confirmation dialog */}
			<AlertDialog open={reverseDialogOpen} onOpenChange={setReverseDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("reverse_confirm_title", {
								defaultValue: "Сторнировать проводки?",
							})}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("reverse_confirm_description", {
								count: selectedRows.length,
								defaultValue: `Будет создано ${selectedRows.length} сторнирующих записей. Это действие нельзя отменить.`,
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								batchReverse.mutate(
									{
										entryIds: selectedRows.map((r) => r.id),
										reason: "",
									},
									{
										onSuccess: (res) => {
											toast.success(
												`${res.successCount} ${t("batch_reversed", { defaultValue: "сторнировано" })}`,
											)
											refreshGrid();
										},
									},
								)
								setReverseDialogOpen(false);
							}}
						>
							{t("toolbar_reverse")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}

function formatAmount(value: number): string {
	return new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value);
}
